import SearchIcon from '@mui/icons-material/Search';
import { Box, Grid, InputBase, Stack } from '@mui/material';
import { debounce, isEmpty } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { save, amplitude, IWorkspace, getWorkspaceUsers, usersAutoCompleteNew } from 'wisestep-api';
import { WsCheckboxList, WsButton } from 'wisestep-base';

import WsCheckBox from '../../../../components/WsCheckBox/WsCheckBox';
import { useWorkspaceContext } from '../../../../context';
import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { assignments } from '../../helper/assignmentHelper';
import { IWorkAssignments, StyledFooter } from '../types';

export interface IRecruitersListProps {
  jobId: string;
  onAssign(): void;
  workAssignments: IWorkAssignments;
}
const RecruitersList = ({ jobId, onAssign, workAssignments }: IRecruitersListProps) => {
  const workspace = useWorkspaceContext();
  const methods = useForm();
  const { watch, setValue } = methods;
  assignments.jobDTOList = [jobId];
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [usersList, setUsersList] = useState<IWorkspace[]>();
  const usersListRef = useRef<IWorkspace[] | null>(null);
  const abortControllerRef = useRef(new AbortController());
  const assignedUsers = workAssignments?.recruiters.map((recruiter) => recruiter?.email);

  const recruitersOptions: {
    [key: string]: string;
  } = {};

  const getRecruiterFullName = (recruiter: IWorkspace) => {
    if (recruiter?.userName || recruiter?.userId) {
      return `${recruiter?.userName ?? ''} (${recruiter?.userId ?? ''})`;
    }
    return '';
  };

  usersList?.forEach((recruiter) => {
    if (recruiter.userId && !assignedUsers?.some((email) => email === recruiter.userId)) {
      recruitersOptions[recruiter.userId] = getRecruiterFullName(recruiter);
    }
  });

  const getWorkspaceUsersQuery = async () => {
    const query = {
      roles: [],
      statuses: ['activated'],
      userIds: [],
      page: 0,
      size: 50,
    };
    const response = await getWorkspaceUsers(query);
    const workspaceUsers: IWorkspace[] = response.data.workspaceUsers;
    return workspaceUsers;
  };

  const getUserList = useCallback(async () => {
    const assignedToData = await getWorkspaceUsersQuery();
    usersListRef.current = assignedToData;
    setUsersList(assignedToData);
  }, []);

  useEffect(() => {
    getUserList();
  }, [getUserList]);

  const saveWorkAssignments = async () => {
    assignments.recruiterDTOList = watch('recruitersList');
    if (assignments?.recruiterDTOList?.length > 0) {
      try {
        await save(assignments);
        amplitude('Job _assignrecruiters_success', {
          clickedOn: 'Assign and Success',
          pageName: 'jobs assign',
          pageUrl: window.location.href,
          workspaceName: workspace.name,
          planType: workspace.planType,
          interfaceType: 'Web',
        });
        setNotification(success(`Job(s) Assigned Successfully.`));
        onAssign();
      } catch {
        setNotification(failed(`Error in Work Assignment. Please try again.`));
      }
    } else {
      setNotification(warning('Please select minimum one team to assign the job!'));
    }
  };
  const handleSelectEvents = (checked: boolean) =>
    checked
      ? setValue('recruitersList', Object.keys(recruitersOptions))
      : setValue('recruitersList', []);

  const onUserInputKey = async (searchText: string) => {
    if (searchText?.length > 2) {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const newAbortController = new AbortController();
      abortControllerRef.current = newAbortController;
      try {
        const response = await usersAutoCompleteNew(
          `${encodeURIComponent(
            searchText
              ?.trim()
              .split(' ')
              .map((name) => (isEmpty(name) ? '' : name + ':*'))
              .join(' & ')
          )}`,
          newAbortController.signal
        );
        const workspaceUsers = response.data;
        const extractedUserData = workspaceUsers
          .filter((user) => user.accepted === true && user.barred === false)
          .map((user) => ({
            userId: user.userId,
            userName: user.userName,
          }));
        setUsersList(extractedUserData);
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    }
  };
  const debouncedUsersInput = (event) => {
    isEmpty(event.target.value)
      ? usersListRef.current &&
        usersListRef.current.length > 0 &&
        setUsersList(usersListRef.current)
      : debounce(onUserInputKey, 150)(event.target.value);
  };

  return (
    <Box>
      <FormProvider {...methods}>
        <Stack direction="row" spacing={1} alignItems={'center'}>
          <SearchIcon />
          <InputBase
            placeholder="Search User"
            name="recruitersFilter"
            onChange={debouncedUsersInput}
            fullWidth
          />
        </Stack>
        <Stack spacing={1}>
          <WsCheckBox
            name="selectAll"
            label="Select All Recruiters"
            bold={true}
            onClick={handleSelectEvents}
          />
          <WsCheckboxList name="recruitersList" options={recruitersOptions} />
        </Stack>
        <StyledFooter>
          <Grid container item justifyContent="space-between" py={1}>
            <WsButton variant="text" size="large">
              {`${
                (methods.watch('recruitersList') && methods.watch('recruitersList').length) ?? 0
              } Selected`}
            </WsButton>
            <WsButton id="assign" variant="contained" size="large" onClick={saveWorkAssignments}>
              Assign
            </WsButton>
          </Grid>
        </StyledFooter>
      </FormProvider>
    </Box>
  );
};

export default RecruitersList;
